import { useEffect, useMemo, useState } from "react";
import {
  SalesQueryDtoSortOrderEnum,
  type DropshippingDto,
  type PurchaseOrderDetailsDto,
  type PurchaseOrderDto,
  type SaleDto,
  type SalesQueryDto,
  type SalesQueryDtoStateEnum,
} from "../../../api";
import { purchaseOrdersService } from "../../api_client";
import { useSales } from "../../sales/hooks/useSales";
import { usePurchaseOrders } from "../../purchase-orders/hooks/usePurchaseOrders";
import { mapPoStatus } from "../../purchase-orders/utils/po-status";

type InternalDeliveryShipment = DropshippingDto & {
  internalDeliveryCostEstimate?: number | null;
  internalDeliveryCostCurrency?: string | null;
  internalDeliveryActualCost?: number | null;
  internalDeliveryActualCostCurrency?: string | null;
  internalDeliveryCostSource?: string | null;
  internalDeliveryCostRecordedAt?: string | null;
};

type PurchaseOrderDetailsWithCosts = PurchaseOrderDetailsDto & {
  dropshipping: InternalDeliveryShipment[];
};

export interface MarginRow {
  saleId: number;
  saleName: string;
  saleDate: string;
  currency: string;
  revenue: number;
  supplierCost: number;
  estimatedDelivery: number;
  actualDelivery: number;
  recordedMargin: number;
  estimatedMargin: number;
  deliveryVariance: number;
  shipmentCount: number;
  purchaseOrderCount: number;
}

export interface MarginSummary {
  currency: string;
  revenue: number;
  supplierCost: number;
  estimatedDelivery: number;
  actualDelivery: number;
  recordedMargin: number;
  estimatedMargin: number;
  deliveryVariance: number;
  shipmentCount: number;
  purchaseOrderCount: number;
}

export interface AdminMarginSnapshot {
  rows: MarginRow[];
  summary: MarginSummary;
  isLoading: boolean;
  isDetailsLoading: boolean;
  error: boolean;
}

const RECENT_SALES_LIMIT = 12;
const RECENT_PURCHASE_ORDERS_LIMIT = 100;
const PURCHASE_ORDER_DETAIL_LIMIT = 60;

const COMPLETED_SALE_STATES = new Set<SalesQueryDtoStateEnum>(["sale", "done"]);

function normalizeKey(value?: string | null) {
  return value?.trim().toLowerCase() ?? "";
}

function sum(values: Array<number | null | undefined>) {
  return values.reduce((total: number, value) => total + Number(value ?? 0), 0);
}

function summarizeShipments(shipments: InternalDeliveryShipment[]) {
  return shipments.reduce(
    (acc, shipment) => {
      acc.estimated += Number(shipment.internalDeliveryCostEstimate ?? 0);
      acc.actual += Number(shipment.internalDeliveryActualCost ?? 0);
      acc.count += 1;
      return acc;
    },
    { estimated: 0, actual: 0, count: 0 },
  );
}

function toSortedPurchaseOrders(orders: PurchaseOrderDto[]) {
  return [...orders].sort((a, b) => {
    const aTs = Date.parse(a.dateOrder ?? "");
    const bTs = Date.parse(b.dateOrder ?? "");
    return bTs - aTs;
  });
}

export function useAdminMarginSnapshot(): AdminMarginSnapshot {
  const salesQuery = useMemo<SalesQueryDto>(
    () => ({
      page: 1,
      pageSize: RECENT_SALES_LIMIT,
      sortBy: "date_order",
      sortOrder: SalesQueryDtoSortOrderEnum.Desc,
    }),
    [],
  );

  const purchaseOrdersQuery = useMemo<SalesQueryDto>(
    () => ({
      page: 1,
      pageSize: RECENT_PURCHASE_ORDERS_LIMIT,
      sortBy: "date_order",
      sortOrder: SalesQueryDtoSortOrderEnum.Desc,
    }),
    [],
  );

  const sales = useSales(salesQuery);
  const purchaseOrders = usePurchaseOrders(purchaseOrdersQuery);

  const recentSales = useMemo(
    () =>
      (sales.data?.data ?? [])
        .filter((sale): sale is SaleDto => COMPLETED_SALE_STATES.has(sale.state as SalesQueryDtoStateEnum))
        .slice(0, RECENT_SALES_LIMIT),
    [sales.data],
  );

  const recentPurchaseOrders = useMemo(
    () =>
      toSortedPurchaseOrders(
        (purchaseOrders.data?.data ?? []).filter(
          (order) => mapPoStatus(order) !== "cancelled",
        ),
      ),
    [purchaseOrders.data],
  );

  const purchaseOrdersBySaleName = useMemo(() => {
    const grouped = new Map<string, PurchaseOrderDto[]>();

    recentPurchaseOrders.forEach((order) => {
      const key = normalizeKey(order.origin);
      if (!key) return;
      const existing = grouped.get(key) ?? [];
      existing.push(order);
      grouped.set(key, existing);
    });

    return grouped;
  }, [recentPurchaseOrders]);

  const detailIds = useMemo(() => {
    const ids = new Set<number>();

    recentSales.forEach((sale) => {
      const related = purchaseOrdersBySaleName.get(normalizeKey(sale.name)) ?? [];
      related.forEach((order) => ids.add(order.id));
    });

    return Array.from(ids).slice(0, PURCHASE_ORDER_DETAIL_LIMIT);
  }, [purchaseOrdersBySaleName, recentSales]);

  const [detailsById, setDetailsById] = useState<Record<number, PurchaseOrderDetailsWithCosts>>({});
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);

  useEffect(() => {
    let active = true;

    if (detailIds.length === 0) {
      setDetailsById({});
      setIsDetailsLoading(false);
      return () => {
        active = false;
      };
    }

    setIsDetailsLoading(true);

    void Promise.all(
      detailIds.map(async (id) => {
        try {
          const details = await purchaseOrdersService.getPurchaseOrderDetails(id);
          return [id, details.data as PurchaseOrderDetailsWithCosts] as const;
        } catch {
          return [id, null] as const;
        }
      }),
    ).then((entries) => {
      if (!active) return;

      const next: Record<number, PurchaseOrderDetailsWithCosts> = {};
      entries.forEach(([id, details]) => {
        if (details) next[id] = details;
      });

      setDetailsById(next);
      setIsDetailsLoading(false);
    });

    return () => {
      active = false;
    };
  }, [detailIds]);

  const rows = useMemo<MarginRow[]>(() => {
    return recentSales
      .map((sale) => {
        const relatedOrders = purchaseOrdersBySaleName.get(normalizeKey(sale.name)) ?? [];
        if (relatedOrders.length === 0) return null;

        const relatedDetails = relatedOrders
          .map((order) => detailsById[order.id])
          .filter((detail): detail is PurchaseOrderDetailsWithCosts => detail != null);

        const revenue = Number(sale.amountTotal ?? 0);
        const supplierCost = sum(relatedOrders.map((order) => order.amountTotal));
        const shipments = relatedDetails.flatMap((detail) => detail.dropshipping ?? []);
        const shipmentSummary = summarizeShipments(shipments);
        const shipmentCount = shipmentSummary.count > 0
          ? shipmentSummary.count
          : sum(relatedOrders.map((order) => order.pickingId?.length ?? 0));
        const estimatedDelivery = shipmentSummary.estimated;
        const actualDelivery = shipmentSummary.actual;
        const recordedMargin = revenue - supplierCost - actualDelivery;
        const estimatedMargin = revenue - supplierCost - estimatedDelivery;

        return {
          saleId: sale.id,
          saleName: sale.name,
          saleDate: sale.dateOrder,
          currency: sale.currency?.name ?? "QAR",
          revenue,
          supplierCost,
          estimatedDelivery,
          actualDelivery,
          recordedMargin,
          estimatedMargin,
          deliveryVariance: actualDelivery - estimatedDelivery,
          shipmentCount,
          purchaseOrderCount: relatedOrders.length,
        };
      })
      .filter((row): row is MarginRow => row != null)
      .sort((a, b) => Date.parse(b.saleDate) - Date.parse(a.saleDate));
  }, [detailsById, purchaseOrdersBySaleName, recentSales]);

  const summary = useMemo<MarginSummary>(() => {
    const currency = rows.find((row) => row.currency)?.currency ?? "QAR";
    const revenue: number = sum(rows.map((row) => row.revenue));
    const supplierCost: number = sum(rows.map((row) => row.supplierCost));
    const estimatedDelivery: number = sum(rows.map((row) => row.estimatedDelivery));
    const actualDelivery: number = sum(rows.map((row) => row.actualDelivery));
    const shipmentCount: number = sum(rows.map((row) => row.shipmentCount));
    const purchaseOrderCount: number = sum(rows.map((row) => row.purchaseOrderCount));

    return {
      currency,
      revenue,
      supplierCost,
      estimatedDelivery,
      actualDelivery,
      recordedMargin: revenue - supplierCost - actualDelivery,
      estimatedMargin: revenue - supplierCost - estimatedDelivery,
      deliveryVariance: actualDelivery - estimatedDelivery,
      shipmentCount,
      purchaseOrderCount,
    };
  }, [rows]);

  return {
    rows,
    summary,
    isLoading: sales.isLoading || purchaseOrders.isLoading,
    isDetailsLoading,
    error: !!sales.error || !!purchaseOrders.error,
  };
}
